import { useCallback, useMemo, type MouseEvent } from 'react';
import { useRouter } from 'next/router';
import ProductCard from '_components/ProductStrip/ProductCard';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackPlpCrossLinkingCardClick } from '_utils/GA4/trackPlpCrossLinkingEvent';
import { useHitSwatches } from '@hooks/useHitSwatches';
import type { HitProps } from '@components/algolia/Hit';

export interface PlpCrossLinkingCardProps {
  /** The Algolia record resolved from the product code merch entered. */
  record: HitProps;
  /** Optional variant SKU that selects which colourway the card shows. */
  variantSku?: string | null;
  locale?: string;
  priority?: boolean;
  index: number;
  coOpBrand?: string | null;
}

/**
 * The colour key for an authored variant SKU. `variants` is keyed by colour, so
 * the SKU has to be looked up before `useHitSwatches` runs — the hook selects by
 * colour, not by SKU. Falls back to the record's own colour when the SKU is
 * absent or unknown, so a stale SKU degrades to the default colourway rather
 * than dropping the card.
 */
const colourKeyForVariantSku = (
  record: HitProps,
  variantSku: string | null | undefined
): string => {
  const sku = variantSku?.trim();

  if (!sku) {
    return record.colour;
  }

  const match = Object.entries(record.variants ?? {}).find(
    ([, variant]) => variant?.sku === sku
  );

  return match?.[0] ?? record.colour;
};

/**
 * One curated product on the PLP cross-linking band.
 *
 * Price selection goes through the same `useHitSwatches` hook the grid's own
 * cards use, so customer-group and member pricing on this band always agrees
 * with the products rendered around it. Presentation is the shared `mini`
 * `ProductCard` on a white surface — the design has no reviews, swatches, tags,
 * or compare control, so none are passed.
 */
const PlpCrossLinkingCard = ({
  record,
  variantSku,
  locale,
  priority = false,
  index,
  coOpBrand,
}: PlpCrossLinkingCardProps) => {
  const { asPath } = useRouter();
  const initialColour = useMemo(
    () => colourKeyForVariantSku(record, variantSku),
    [record, variantSku]
  );

  const { activeVariant } = useHitSwatches(
    initialColour,
    record.variants,
    {
      price: record.price,
      salePrice: record.salePrice,
      image: record.image,
      imageTransparent: record.imageTransparent,
      badge: record.badge ?? null,
      badgeGuest: record.badgeGuest ?? null,
      sku: record.sku,
    },
    locale
  );

  const queryString = new URLSearchParams(
    activeVariant?.colourQuerySlug ?? ''
  ).toString();

  // `record.url` is already a complete, locale-correct path in the index.
  const productUrl = `${record.url}${queryString ? `?${queryString}` : ''}`;

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const data = {
        interactionLocation: asPath.split('?')[0],
        linkUrl: productUrl,
        linkText: record.title || '',
        coOpBrand: coOpBrand || null,
        index,
      };

      // A modified click keeps this page alive, so the hit is not at risk and
      // navigation must not be hijacked.
      if (isModifiedClick(e)) {
        trackPlpCrossLinkingCardClick({ data });
        return;
      }

      e.preventDefault();
      trackPlpCrossLinkingCardClick({
        data,
        eventCallback: () => {
          window.location.href = productUrl;
        },
      });
    },
    [asPath, productUrl, record.title, coOpBrand, index]
  );

  return (
    <ProductCard
      mini
      onTint
      priority={priority}
      productImage={
        activeVariant?.imageTransparent ||
        activeVariant?.image ||
        record.imageTransparent ||
        record.image ||
        '/images/pdp/product-placeholder.jpg'
      }
      brand={record.brand || ''}
      displayName={record.title || ''}
      productUrl={productUrl}
      displayPrice={activeVariant?.priceFormatted || ''}
      salePrice={
        activeVariant?.priceRange?.salePrice ||
        activeVariant?.salePriceFormatted ||
        ''
      }
      isOnSaleOrClearance={Boolean(
        activeVariant?.salePriceFormatted ||
          activeVariant?.priceRange?.salePrice
      )}
      productCode={record.parentSku}
      trackClick={handleClick}
    />
  );
};

export default PlpCrossLinkingCard;
